import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  TableSortLabel,
  Tooltip,
  Skeleton,
  Switch,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import EditIcon from '@mui/icons-material/Edit';
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { visuallyHidden } from '@mui/utils';
import amazonLogo from '../../../assets/svgs/amazon.svg'
import myntraLogo from '../../../assets/svgs/myntra.svg'
import flipkartLogo from '../../../assets/svgs/flipkart.svg'
import adyogiLogo from "../../../assets/images/Adyogi_logoz.svg";
import CustomPagination from "../../../components/CustomPagination/CustomPagination";
import { numberFormatter } from "../../../helper/Reports/common";
import moment from "moment";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];

function SmartAdsTable({
  dataHeads = [],
  searchfilteredData = [],
  allFetchLoaders = {},
  onSortClick,
  handleEdit,
  handleLogs,
  summaryData = [],
  handleSwitchChange,
  handleCampaignMapping
}) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    onSortClick && onSortClick(property, isAsc ? "desc" : "asc");
  };

  const logo = {
    amazon: amazonLogo,
    myntra: myntraLogo,
    flipkart: flipkartLogo,
    adyogi: adyogiLogo,
  };
  const userPlatform =
    JSON.parse(localStorage.getItem("user_platform")) || "amazon";

  const handleChangePage = (newPage) => setPage(newPage);

  const handlePageLimitChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const visibleRows = searchfilteredData.slice(
    (page - 1) * rowsPerPage,
    (page - 1) * rowsPerPage + rowsPerPage
  );

  if (visibleRows.length === 0 && !allFetchLoaders.mainPageLoader) {
    return (
      <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "250px", // adjust as needed
        color: "text.secondary",
        gap: 1.5,
      }}
    >
      <SearchOffIcon sx={{ fontSize: 48, color: "grey.500" }} />
      <Typography variant="subtitle1">No Data Found</Typography>
    </Box>
    );
  }

const stickyLeftStyle = (left=0, bgColor="#ffffff") => ({
  position: "sticky",
  left,
  zIndex: 3,
  background: bgColor,
  borderRight: "1px solid #ddd",
  color: "#00000099",
  fontWeight: 600,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
});

const stickyRightStyle = (right=0, bgColor="#ffffff") => ({
  position: "sticky",
  right,
  zIndex: 3,
  background: bgColor,
  borderLeft: "1px solid #ddd",
  fontWeight: 600,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
  textAlign: "center",
  color: "#00000099",
});

const commonCellStyle = (isActive, bgColor="#F7F6FE") => ({
  background: isActive ? bgColor : "#ffffff",
  fontWeight: 600,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
  padding: "6px 16px",
  borderRight: "1px solid #ddd",
  minWidth: 120,
});

const zIndexes = {
  header: 5,
  summary: 4,
  stickyColumn: 6,
};

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "auto",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "none",
      }}
    >
      <TableContainer sx={{ maxHeight: 600, minHeight: 400 }}>
        <Table stickyHeader size="small" sx={{ borderCollapse: "separate" }}>
          <TableHead>
            {/* Header Row */}
            <TableRow
              sx={{ zIndex: zIndexes.header, position: "sticky", top: 0 }}
            >
              <TableCell
                sx={{
                  ...stickyLeftStyle(0),
                  padding: "16px",
                  minWidth: 80,
                  zIndex: zIndexes.stickyColumn,
                  textAlign: "center",
                  borderBottom: "none", // hide bottom border to avoid double-border with summary
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  ...stickyLeftStyle(80),
                  padding: "6px 16px",
                  minWidth: 200,
                  zIndex: zIndexes.stickyColumn,
                  borderBottom: "none",
                }}
              >
                Product Set
              </TableCell>

              {dataHeads.map((th, index) =>
                !th.isDefaultHead ? (
                  <TableCell
                    key={th.id}
                    align={th.isHeadRightAlign ? "right" : "left"}
                    sx={{
                      ...commonCellStyle(orderBy === th.id),
                      borderBottom: "none",
                      zIndex: zIndexes.header,
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap={1}
                      alignItems={
                        th.isHeadRightAlign ? "flex-end" : "flex-start"
                      }
                    >
                      {th.isHeadWithIcon === "sort" ? (
                        <TableSortLabel
                          active={orderBy === th.id}
                          direction={orderBy === th.id ? order : "asc"}
                          onClick={() => handleSort(th.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            flexDirection: "row-reverse",
                            justifyContent: "space-between",
                          }}
                        >
                          {th.id !== "created_at" && (
                            <img
                              src={logo[userPlatform] || adyogiLogo}
                              alt="platform"
                              width={14}
                              height={14}
                              style={{ display: "block" }}
                            />
                          )}
                          {orderBy === th.id && (
                            <Box sx={visuallyHidden}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          )}
                        </TableSortLabel>
                      ) : (
                        <img
                          src={logo[userPlatform] || adyogiLogo}
                          alt="platform"
                          width={14}
                          height={14}
                          style={{ display: "block" }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#00000099",
                          textAlign: th.isHeadRightAlign ? "right" : "left",
                        }}
                      >
                        {th.fieldName}
                      </Typography>
                    </Box>
                  </TableCell>
                ) : null
              )}

              <TableCell
                sx={{
                  ...stickyRightStyle(0),
                  zIndex: zIndexes.stickyColumn,
                  borderBottom: "none",
                }}
              >
                Action
              </TableCell>
            </TableRow>

            {/* Summary Row */}
            <TableRow
              sx={{
                position: "sticky",
                top: 56,
                zIndex: zIndexes.summary,
                backgroundColor: "#f9f9f9",
              }}
            >
              <TableCell
                sx={{
                  ...stickyLeftStyle(0, "#f9f9f9"),
                  padding: "16px",
                  minWidth: 80,
                  zIndex: zIndexes.stickyColumn,
                  borderTop: "1px solid #ddd",
                }}
              />
              <TableCell
                sx={{
                  ...stickyLeftStyle(80, "#f9f9f9"),
                  padding: "16px",
                  minWidth: 200,
                  zIndex: zIndexes.stickyColumn,
                  borderTop: "1px solid #ddd",
                }}
              >
                Summary
              </TableCell>

              {dataHeads.map((head) => {
                if (head.isDefaultHead) return null;
                const value =
                  head.id === "created_at" || allFetchLoaders?.mainPageLoader
                    ? null
                    : summaryData?.[0]?.[head.id];
                return (
                  <TableCell
                    key={`summary-${head.id}`}
                    align={head.isHeadRightAlign ? "right" : "left"}
                    sx={{
                      fontSize: "0.75rem",
                      padding: "16px",
                      background: orderBy === head.id ? "#EAE6FD" : "#f9f9f9",
                      borderRight: "1px solid #ddd",
                      minWidth: 120,
                      fontWeight: 600,
                      zIndex: zIndexes.summary,
                      borderTop: "1px solid #ddd",
                    }}
                  >
                    {value
                      ? head.isCurrencyFormat
                        ? numberFormatter(value, true)
                        : numberFormatter(value, false, head?.type)
                      : "-"}
                  </TableCell>
                );
              })}

              <TableCell
                sx={{
                  ...stickyRightStyle(0, "#f9f9f9"),
                  padding: "16px",
                  zIndex: zIndexes.stickyColumn,
                  borderTop: "1px solid #ddd",
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {allFetchLoaders?.mainPageLoader
              ? [...Array(10)].map((_, idx) => (
                  <TableRow key={`skeleton-${idx}`}>
                    {/* Status Skeleton */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        background: "white",
                        zIndex: 2,
                        minWidth: 80,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Skeleton variant="rounded" width={40} height={12} />
                    </TableCell>

                    {/* Product Set Skeleton */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 80,
                        background: "white",
                        zIndex: 2,
                        minWidth: 200,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Skeleton variant="text" width="60%" height={20} />
                    </TableCell>

                    {/* Dynamic DataHeads Skeleton */}
                    {dataHeads
                      .filter((head) => !head.isDefaultHead)
                      .map((head) => (
                        <TableCell
                          key={`skeleton-${idx}-${head.id}`}
                          align={head.isHeadRightAlign ? "right" : "left"}
                          sx={{
                            fontSize: "0.75rem",
                            padding: "16px",
                            background: "white",
                            borderRight: "1px solid #ddd",
                            minWidth: 120,
                          }}
                        >
                          <Skeleton variant="text" width="80%" height={20} />
                        </TableCell>
                      ))}

                    {/* Actions Skeleton */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 2,
                        borderLeft: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Box display="flex" gap={1}>
                        {[...Array(3)].map((_, iconIdx) => (
                          <Skeleton
                            key={iconIdx}
                            variant="circular"
                            width={24}
                            height={24}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              : visibleRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:hover td": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {/* Status */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        background: "white",
                        zIndex: 2,
                        minWidth: 80,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Switch
                        checked={row.status?.toLowerCase() === "enabled"}
                        onChange={() => handleSwitchChange(row)}
                        size="small"
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 80,
                        background: "white",
                        zIndex: 2,
                        minWidth: 200,
                        borderRight: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Tooltip title={row.product_set || ""} arrow>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 170, // or adjust to fit your layout
                          }}
                        >
                          {row.product_set}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Dynamic Campaign Stat Cells */}
                    {(row.campaignStat?.length ? row.campaignStat : [{}]).map(
                      (stat, statIdx) =>
                        dataHeads.map((head) =>
                          !head.isDefaultHead ? (
                            <TableCell
                              key={`${row.id}-${statIdx}-${head.id}`}
                              align={head.isHeadRightAlign ? "right" : "left"}
                              sx={{
                                fontSize: "0.75rem",
                                padding: "16px",
                                background:
                                  orderBy === head.id ? "#F7F6FE" : "white",
                                borderRight: "1px solid #ddd",
                                minWidth: 120,
                              }}
                            >
                              {head.id === "created_at"
                                ? row?.created_at || stat?.created_at
                                  ? moment
                                      .utc(row?.created_at || stat?.created_at)
                                      .local()
                                      .format("DD MMM YYYY hh:mm a")
                                  : "-"
                                : stat?.[head.id]
                                ? head.isCurrencyFormat
                                  ? numberFormatter(stat[head.id], true)
                                  : numberFormatter(stat[head.id], false, head?.type)
                                : "-"}
                            </TableCell>
                          ) : null
                        )
                    )}

                    {/* Actions */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        background: "white",
                        zIndex: 2,
                        borderLeft: "1px solid #ddd",
                        padding: "16px",
                      }}
                    >
                      <Box display="flex" gap={1}>
                        <Tooltip title="Map Campaign">
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleCampaignMapping({
                              id: row.id,
                              entity_populate_list: row.entity_populate_list || []
                            })
                            }}
                            sx={{
                              backgroundColor: row?.entity_populate_list?.length
                                ? "#e0f5e9"
                                : "#ede9fe",
                              color: row?.entity_populate_list?.length
                                ? row?.brandColor || "#2e7d32"
                                : "#6148f6",
                              "&:hover": {
                                backgroundColor: row?.entity_populate_list?.length
                                  ? "#c8e6c9"
                                  : "#ddd6fe",
                              },
                            }}
                          >
                            <InsertLinkIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Product Set">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row.id)}
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="View Logs">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleLogs(row.id, row?.product_set || "")
                            }
                          >
                            <HistoryIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {(searchfilteredData.length > 0 || page > 1) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 2,
            px: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#4f5366",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "12px" }}>
              Page Size
            </Typography>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={rowsPerPage}
                onChange={handlePageLimitChange}
                disabled={allFetchLoaders?.mainPageLoader}
                sx={{
                  height: 36,
                  fontSize: "12px",
                  color: "#333333",
                }}
                MenuProps={{ disableScrollLock: true }}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{ fontSize: "12px" }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <CustomPagination
            currentPage={page}
            totalPages={Math.ceil(searchfilteredData.length / rowsPerPage) || 1}
            onPageChange={handleChangePage}
            lazyLoading={allFetchLoaders?.mainPageLoader}
            totalItems={searchfilteredData.length}
            itemsPerPage={rowsPerPage}
          />
        </Box>
      )}
    </Paper>
  );
};

export default SmartAdsTable;